import { createContext, useContext, type ReactNode } from 'react'

type ResolveMediaUrl = (src: string) => string

const MediaUrlContext = createContext<ResolveMediaUrl>((src) => src)

/** Local upload previews win over stored URLs. The resolver is built here from its inputs, so it keeps its identity
 * until they change: a context value that changed on every editor render made React search every memoized row for
 * consumers on each keystroke (propagateParentContextChanges). */
export function MediaUrlProvider({
  children,
  previews,
  fileHandler,
}: {
  children: ReactNode
  previews: Readonly<Record<string, string>>
  fileHandler: { getUrl: ResolveMediaUrl }
}) {
  const resolveStoredUrl: ResolveMediaUrl = src => previews[src] ?? fileHandler.getUrl(src)
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values -- React Compiler owns memoization; no manual performance hooks
    <MediaUrlContext.Provider value={resolveStoredUrl}>
      {children}
    </MediaUrlContext.Provider>
  )
}

export function useMediaUrl(): ResolveMediaUrl {
  const resolveStoredUrl = useContext(MediaUrlContext)
  return (src) => resolveMediaDisplayUrl(src, resolveStoredUrl)
}

/**
 * Absolute browser-ready sources are already display URLs. Only opaque bucket
 * ids and relative storage paths cross the consumer's stored-media boundary.
 */
export function resolveMediaDisplayUrl(
  src: string,
  resolveStoredUrl?: ResolveMediaUrl,
): string {
  if (isBrowserMediaUrl(src)) return src
  return resolveStoredUrl?.(src) ?? src
}

function isBrowserMediaUrl(src: string): boolean {
  return /^(?:https?:|blob:|data:)/iu.test(src) || src.startsWith('/')
}
